import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Context } from "../../../../Context";
import operationData from "../../../../../Operation/data.json";
import {
  createItem,
  createUser,
  deleteDiscount,
  deleteItem,
  deleteUser,
  fetchDiscount,
  fetchItems,
  fetchOrders,
  fetchStoreStatus,
  fetchUsers,
  saveDiscount,
  updateItem,
  updateOrderStatus,
  updateStoreStatus,
  updateUser,
} from "../../../../utils/foodDeliveryApi";
import {
  MdDeliveryDining,
  MdFastfood,
  MdPeople,
  MdOutlineStorefront,
  MdDiscount,
} from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import FoodDeliveryTabs from "./FoodDeliveryTabs";

const emptyStaffForm = {
  userName: "",
  phoneNumber: "",
  emailId: "",
  role: "delivery",
};

const emptyItemForm = {
  itemName: "",
  price: "",
  category: "",
  description: "",
};

const emptyDiscountForm = {
  discount: "",
  type: "flat",
};

const emptyOrderStatusForm = {
  status: "preparing",
  minutes: "",
};

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  const candidate = response?.data || response?.items || response?.result || response?.body;
  return Array.isArray(candidate) ? candidate : [];
};

const FoodDeliveryAdmin = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || "";

  const [loading, setLoading] = useState(false);
  const [storeOn, setStoreOn] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [activePanel, setActivePanel] = useState("overview");
  const [staffForm, setStaffForm] = useState(emptyStaffForm);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [discountForm, setDiscountForm] = useState(emptyDiscountForm);
  const [orderStatusForm, setOrderStatusForm] = useState({});

  const summary = useMemo(() => ({
    totalOrders: Array.isArray(orders) ? orders.length : 0,
    totalUsers: Array.isArray(users) ? users.length : 0,
    totalItems: Array.isArray(items) ? items.length : 0,
    activeOrders: Array.isArray(orders)
      ? orders.filter((order) => !["delivered", "cancelled"].includes(String(order.status || "").toLowerCase())).length
      : 0,
  }), [orders, users, items]);

  const loadData = async () => {
    if (!activeInstitution) {
      setError("Food delivery institution is not configured.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [storeResponse, discountResponse, ordersResponse, usersResponse, itemsResponse] = await Promise.allSettled([
        fetchStoreStatus(activeInstitution),
        fetchDiscount(activeInstitution),
        fetchOrders(activeInstitution),
        fetchUsers(activeInstitution),
        fetchItems(activeInstitution),
      ]);

      if (storeResponse.status === "fulfilled") {
        const storeStatus = storeResponse.value?.data?.status || storeResponse.value?.status;
        setStoreOn(String(storeStatus || "").toLowerCase() === "on");
      }

      if (discountResponse.status === "fulfilled") {
        setDiscount(discountResponse.value?.data || discountResponse.value || null);
      }

      if (ordersResponse.status === "fulfilled") {
        setOrders(normalizeListResponse(ordersResponse.value));
      }

      if (usersResponse.status === "fulfilled") {
        setUsers(normalizeListResponse(usersResponse.value));
      }

      if (itemsResponse.status === "fulfilled") {
        setItems(normalizeListResponse(itemsResponse.value));
      }

      const rejected = [storeResponse, discountResponse, ordersResponse, usersResponse, itemsResponse].filter((entry) => entry.status === "rejected");
      if (rejected.length > 0) {
        setMessage("Loaded available sections. Some food delivery requests failed.");
      } else {
        setMessage("Food delivery admin data loaded successfully.");
      }
    } catch (err) {
      setError(err?.message || "Unable to load food delivery admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeInstitution]);

  const handleToggleStore = async () => {
    setLoading(true);
    setError("");
    try {
      const nextStatus = storeOn ? "off" : "on";
      await updateStoreStatus(activeInstitution, nextStatus);
      setStoreOn(!storeOn);
      setMessage(`Store is now ${nextStatus === "on" ? "online" : "offline"}.`);
    } catch (err) {
      setError(err?.message || "Failed to update store status.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDiscount = async () => {
    setLoading(true);
    setError("");
    try {
      await saveDiscount(activeInstitution, {
        ...discountForm,
        discount: Number(discountForm.discount),
      });
      setMessage("Discount updated.");
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to save discount.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDiscount = async () => {
    setLoading(true);
    setError("");
    try {
      await deleteDiscount(activeInstitution);
      setDiscount(null);
      setDiscountForm(emptyDiscountForm);
      setMessage("Discount deleted.");
    } catch (err) {
      setError(err?.message || "Failed to delete discount.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async () => {
    setLoading(true);
    setError("");
    try {
      await createUser(activeInstitution, staffForm);
      setStaffForm(emptyStaffForm);
      setMessage("Staff member created.");
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to create staff member.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStaff = async (staff) => {
    setLoading(true);
    setError("");
    try {
      await updateUser(activeInstitution, staff);
      setMessage("Staff member updated.");
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to update staff member.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStaff = async (cognitoId) => {
    setLoading(true);
    setError("");
    try {
      await deleteUser(activeInstitution, cognitoId);
      setMessage("Staff member deleted.");
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to delete staff member.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async () => {
    setLoading(true);
    setError("");
    try {
      await createItem(activeInstitution, {
        ...itemForm,
        price: Number(itemForm.price),
      });
      setItemForm(emptyItemForm);
      setMessage("Menu item created.");
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to create menu item.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItem = async (item) => {
    setLoading(true);
    setError("");
    try {
      await updateItem(activeInstitution, item.itemId || item.id, item);
      setMessage("Menu item updated.");
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to update menu item.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    setLoading(true);
    setError("");
    try {
      await deleteItem(activeInstitution, itemId);
      setMessage("Menu item deleted.");
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to delete menu item.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = async (orderId) => {
    const nextStatus = orderStatusForm[orderId]?.status || "preparing";
    const minutes = orderStatusForm[orderId]?.minutes || "";
    setLoading(true);
    setError("");
    try {
      await updateOrderStatus(activeInstitution, orderId, {
        status: nextStatus,
        deliveryTime: nextStatus === "preparing" && minutes ? Number(minutes) : null,
      });
      setMessage("Order status updated.");
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to update order status.");
    } finally {
      setLoading(false);
    }
  };

  const sectionButtonClass = (section) =>
    `rounded-full px-4 py-2 text-sm font-medium transition-colors ${
      activePanel === section ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`;

  const renderOverview = () => (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-500">Store status</p>
            <h2 className="mt-1 text-2xl font-semibold text-gray-900">
              {storeOn ? "Online" : "Offline"}
            </h2>
          </div>
          <MdOutlineStorefront className={`text-4xl ${storeOn ? "text-green-600" : "text-red-500"}`} />
        </div>
        <button
          onClick={handleToggleStore}
          className="mt-6 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
        >
          {storeOn ? "Go Offline" : "Go Online"}
        </button>
        <p className="mt-3 text-sm text-gray-500">
          Store controls use the food-delivery backend settings endpoint.
        </p>
      </div>

      <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">Active institution</p>
            <h2 className="mt-1 text-2xl font-semibold text-gray-900">{activeInstitution}</h2>
          </div>
          <MdDeliveryDining className="text-4xl text-orange-500" />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
          <StatCard label="Orders" value={summary.totalOrders} />
          <StatCard label="Active" value={summary.activeOrders} />
          <StatCard label="Users" value={summary.totalUsers} />
          <StatCard label="Items" value={summary.totalItems} />
        </div>
      </div>
    </div>
  );

  const renderOrders = () => (
    <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xl font-semibold text-gray-900">Orders</h3>
        <MdFastfood className="text-2xl text-orange-500" />
      </div>
      <div className="mt-4 space-y-4">
        {orders.length === 0 && <EmptyState text="No orders found for this institution." />}
        {orders.map((order, index) => {
          const orderId = order.orderId || order.id || index;
          return (
            <div key={orderId} className="rounded-xl border border-gray-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-gray-900">Order #{orderId}</p>
                  <p className="text-sm text-gray-500">Status: {order.status || "pending"}</p>
                </div>
                <p className="text-sm font-medium text-gray-700">
                  ₹{Number(order.totalAmount || order.amount || 0).toFixed(2)}
                </p>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
                <label className="block text-sm text-gray-600">
                  Status
                  <select
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={orderStatusForm[orderId]?.status || order.status || "preparing"}
                    onChange={(event) =>
                      setOrderStatusForm((prev) => ({
                        ...prev,
                        [orderId]: {
                          ...(prev[orderId] || emptyOrderStatusForm),
                          status: event.target.value,
                        },
                      }))
                    }
                  >
                    <option value="preparing">Preparing</option>
                    <option value="ready">Ready</option>
                    <option value="out for delivery">Out for delivery</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </label>
                <label className="block text-sm text-gray-600">
                  Delivery minutes
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    type="number"
                    min="0"
                    value={orderStatusForm[orderId]?.minutes || ""}
                    onChange={(event) =>
                      setOrderStatusForm((prev) => ({
                        ...prev,
                        [orderId]: {
                          ...(prev[orderId] || emptyOrderStatusForm),
                          minutes: event.target.value,
                        },
                      }))
                    }
                    placeholder="Optional"
                  />
                </label>
                <button
                  onClick={() => handleUpdateOrder(orderId)}
                  className="rounded-lg bg-gray-900 px-4 py-2 text-white hover:bg-gray-800"
                >
                  Update
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xl font-semibold text-gray-900">Staff management</h3>
        <MdPeople className="text-2xl text-blue-500" />
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="User name" value={staffForm.userName} onChange={(event) => setStaffForm((prev) => ({ ...prev, userName: event.target.value }))} />
        <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Phone number" value={staffForm.phoneNumber} onChange={(event) => setStaffForm((prev) => ({ ...prev, phoneNumber: event.target.value }))} />
        <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Email address" value={staffForm.emailId} onChange={(event) => setStaffForm((prev) => ({ ...prev, emailId: event.target.value }))} />
        <select className="rounded-lg border border-gray-300 px-3 py-2" value={staffForm.role} onChange={(event) => setStaffForm((prev) => ({ ...prev, role: event.target.value }))}>
          <option value="delivery">Delivery</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
        </select>
      </div>
        <button onClick={handleCreateStaff} className="mt-4 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium">
          Create staff
        </button>
      <div className="mt-6 space-y-3">
        {users.length === 0 && <EmptyState text="No staff users found." />}
        {users.map((user) => (
          <div key={user.cognitoId || user.phoneNumber || user.emailId} className="rounded-xl border border-gray-200 p-4">
            <div className="grid gap-3 md:grid-cols-4">
              <input className="rounded-lg border border-gray-300 px-3 py-2" value={user.userName || ""} onChange={(event) => setUsers((prev) => prev.map((entry) => entry === user ? { ...entry, userName: event.target.value } : entry))} />
              <input className="rounded-lg border border-gray-300 px-3 py-2" value={user.phoneNumber || ""} onChange={(event) => setUsers((prev) => prev.map((entry) => entry === user ? { ...entry, phoneNumber: event.target.value } : entry))} />
              <input className="rounded-lg border border-gray-300 px-3 py-2" value={user.emailId || ""} onChange={(event) => setUsers((prev) => prev.map((entry) => entry === user ? { ...entry, emailId: event.target.value } : entry))} />
              <input className="rounded-lg border border-gray-300 px-3 py-2" value={user.role || "delivery"} onChange={(event) => setUsers((prev) => prev.map((entry) => entry === user ? { ...entry, role: event.target.value } : entry))} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => handleUpdateStaff(user)} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium">Save</button>
              <button onClick={() => handleDeleteStaff(user.cognitoId)} className="bg-red-600 px-4 py-2 text-white rounded-lg">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderItems = () => (
    <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xl font-semibold text-gray-900">Menu management</h3>
        <MdOutlineStorefront className="text-2xl text-emerald-500" />
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Item name" value={itemForm.itemName} onChange={(event) => setItemForm((prev) => ({ ...prev, itemName: event.target.value }))} />
        <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Price" type="number" value={itemForm.price} onChange={(event) => setItemForm((prev) => ({ ...prev, price: event.target.value }))} />
        <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Category" value={itemForm.category} onChange={(event) => setItemForm((prev) => ({ ...prev, category: event.target.value }))} />
        <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Description" value={itemForm.description} onChange={(event) => setItemForm((prev) => ({ ...prev, description: event.target.value }))} />
      </div>
      <button onClick={handleCreateItem} className="mt-4 rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700">
        Create item
      </button>
      <div className="mt-6 space-y-3">
        {items.length === 0 && <EmptyState text="No menu items found." />}
        {items.map((item) => (
          <div key={item.itemId || item.id || item.itemName} className="rounded-xl border border-gray-200 p-4">
            <div className="grid gap-3 md:grid-cols-4">
              <input className="rounded-lg border border-gray-300 px-3 py-2" value={item.itemName || ""} onChange={(event) => setItems((prev) => prev.map((entry) => entry === item ? { ...entry, itemName: event.target.value } : entry))} />
              <input className="rounded-lg border border-gray-300 px-3 py-2" value={item.price || ""} onChange={(event) => setItems((prev) => prev.map((entry) => entry === item ? { ...entry, price: event.target.value } : entry))} />
              <input className="rounded-lg border border-gray-300 px-3 py-2" value={item.category || ""} onChange={(event) => setItems((prev) => prev.map((entry) => entry === item ? { ...entry, category: event.target.value } : entry))} />
              <input className="rounded-lg border border-gray-300 px-3 py-2" value={item.description || ""} onChange={(event) => setItems((prev) => prev.map((entry) => entry === item ? { ...entry, description: event.target.value } : entry))} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => handleUpdateItem(item)} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium">Save</button>
              <button onClick={() => handleDeleteItem(item.itemId || item.id)} className="bg-red-600 px-4 py-2 text-white rounded-lg">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDiscount = () => (
    <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xl font-semibold text-gray-900">Discounts</h3>
        <MdDiscount className="text-2xl text-violet-500" />
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Discount value" type="number" value={discountForm.discount} onChange={(event) => setDiscountForm((prev) => ({ ...prev, discount: event.target.value }))} />
        <select className="rounded-lg border border-gray-300 px-3 py-2" value={discountForm.type} onChange={(event) => setDiscountForm((prev) => ({ ...prev, type: event.target.value }))}>
          <option value="flat">Flat</option>
          <option value="percentage">Percentage</option>
        </select>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={handleSaveDiscount} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium">Save discount</button>
        <button onClick={handleDeleteDiscount} className="bg-red-600 px-4 py-2 text-white rounded-lg">Delete discount</button>
      </div>
      <div className="mt-4 text-sm text-gray-600">
        Current discount: {discount ? JSON.stringify(discount) : "None"}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-white to-orange-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-extrabold text-[#1F2937]">{activeInstitution || "Food delivery"}</h1>
                <p className="mt-1 text-sm text-gray-500">Manage store controls, staff, menu items, discounts, and live orders from one dashboard.</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={loadData} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200">
                  <FiRefreshCw /> Refresh
                </button>
              </div>
            </div>
          </div>

          {(message || error) && (
            <div className={`rounded-2xl px-4 py-3 text-sm ${error ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
              {error || message}
            </div>
          )}

          {loading && <div className="text-sm text-gray-500">Loading food delivery data...</div>}

          {activePanel === "overview" && renderOverview()}
          {activePanel === "orders" && renderOrders()}
          {activePanel === "users" && renderUsers()}
          {activePanel === "items" && renderItems()}
          {activePanel === "discount" && renderDiscount()}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
    {text}
  </div>
);

export default FoodDeliveryAdmin;
